import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'formik';
import isEqual from 'lodash.isequal';
import debounce from 'lodash.debounce';
import safeExec from '../../../../utils/safeExec';

const localStorage = window.localStorage;
const DEBOUNCE = 300;

/**
 * @param {string} name Human readable submission form type name
 * @param {string} id Encoded Id
 */
const getKey = (name, id) => `form__${name}__${id}`;

const saveForm = safeExec(
  debounce(({ formik, name, id }) => {
    const key = getKey(name, id);

    // Remove some fields from localStorage copy
    //  - `isSubmitting` prevents submission button being disabled
    //  - `errors`, `touched`, and `submitCount` prevent error messages from appearing upon returning
    const {
      isSubmitting,
      errors,
      touched,
      submitCount,
      ...formikData
    } = formik; // eslint-disable-line no-unused-vars

    // If submitted, clear key from localStorage. Otherwise update localStorage.
    if (isSubmitting && !Object.keys(errors).length) {
      clearKeys({ name, id });
    } else {
      localStorage.setItem(key, JSON.stringify(formikData));
    }
  }, DEBOUNCE)
);

const clearKeys = safeExec(({ name, id }) => {
  const formKey = getKey(name, id);

  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith(formKey)) {
      localStorage.removeItem(key);
    }
  });
});

const applyForm = safeExec((formik, name, id) => {
  const key = getKey(name, id);
  const state = localStorage.getItem(key);

  if (state && state !== null) {
    formik.setFormikState(JSON.parse(state), () => {
      clearKeys({ name, id });
    });
  }
});

const FormAutosave = ({
  submission: { name, id },
  formik,
  enabled = false,
}) => {
  if (!enabled) {
    return null;
  }

  const prevFormik = useRef(formik).current;

  useEffect(() => {
    applyForm(formik, name, id);
  }, []); // Empty array calls this effect only once

  useEffect(() => {
    // If formik is different and dirty, save to localStorage.
    if (!isEqual(prevFormik, formik) && formik.dirty) {
      saveForm({ formik, name, id });
    }
  }, [formik]); // Only run this effect if formik changes

  return null;
};

FormAutosave.propTypes = {
  formik: PropTypes.object.isRequired,
  submission: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
  }).isRequired,
};

export default connect(FormAutosave);
